import { IBookingGiftClaimReminder, IOfferCallout } from "@/api_framework/api_modals/Bookings";
import OfferCalloutBadge from "@/app/components/ui/Badges/OfferCalloutBadge";
import { GiftIcon } from "lucide-react";

const DEFAULT_CLAIM_MESSAGE = "Ask the customer to open the Vetic app and claim this gift";
const DEFAULT_CLAIMED_MESSAGE = "Claimed! Avail this gift at the clinic";

// Drawn like a gift tag on a line: same icon and gradient the backend sends in offer_callout
const GIFTS_HEADING_CALLOUT: IOfferCallout = {
  txt_sft: "Gifts available for this user",
  icon: "https://vetic-img.s3.ap-south-1.amazonaws.com/quick_commerce//misc/local_offer.svg",
  fg_gradient_type: "radial",
  fg_gradient_colors: ["#F77A55FF", "#FB9E6AF6", "#FFC17FEE", "#FFBA56DB", "#FFB32DC7"],
  fg_gradient_stops: [0, 0.3, 0.6, 0.8, 1],
  fg_gradient_begin_alignment: "center_left",
};

interface IBookingGiftsSectionProps {
  giftClaimReminders: IBookingGiftClaimReminder[];
}

// gift_details: the gifts offered with the booking, both still to claim in the app (ELIGIBLE) and already CLAIMED.
const BookingGiftsSection: React.FC<IBookingGiftsSectionProps> = ({ giftClaimReminders }) => {
  if (giftClaimReminders.length === 0) {
    return null;
  }

  return (
    <section className="mb-4 bg-white p-3">
      <h3>
        <OfferCalloutBadge callout={GIFTS_HEADING_CALLOUT} size="medium" />
      </h3>
      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
        {giftClaimReminders.map((reminder) => {
          const isClaimed = reminder.status === "CLAIMED";
          const statusColorClassName = isClaimed ? "bg-[#e6f7e0] text-[#3b7c3e]" : "bg-[#fee4e2] text-[#c4320a]";
          return (
            <div
              key={reminder.gift_id}
              className="flex flex-col gap-1.5 rounded-[12px] border border-[#ececec] bg-[#fbfaf7] p-1.5"
            >
              <div className="flex flex-1 items-center gap-2.5 rounded-[10px] bg-white p-2.5 shadow-[0_1px_3px_rgba(16,24,40,0.1)]">
                {reminder.gift_image ? (
                  <img src={reminder.gift_image} alt="" className="h-10 w-10 shrink-0 rounded-lg object-cover" />
                ) : (
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#FFEEF8] text-[#f05b72]">
                    <GiftIcon size={18} />
                  </div>
                )}
                <p className="min-w-0 flex-1 text-[12px] font-semibold leading-4 text-[#101828]">
                  {reminder.heading || reminder.gift_tag || "Gift"}
                </p>
                <span
                  className={`shrink-0 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${statusColorClassName}`}
                >
                  {isClaimed ? "Claimed" : "Not claimed"}
                </span>
              </div>
              <div
                className={`w-full rounded-lg px-2 py-1.5 text-center text-[12px] font-semibold ${statusColorClassName}`}
              >
                {reminder.message || (isClaimed ? DEFAULT_CLAIMED_MESSAGE : DEFAULT_CLAIM_MESSAGE)}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default BookingGiftsSection;
